/**
 * /admin/live — every attempt that is running right now, one row each.
 *
 * This is the screen an invigilator sits on for the length of a sitting, so it
 * is built around two things the other admin screens do not have to care about:
 *
 * **It must never blank.** The list is polled every 10s, but a poll replaces
 * the data in place — no `useAsync`, because its per-request `loading` flag
 * would flash the whole table away four times a minute while someone is trying
 * to read a clock on it. `loading` here means "we have nothing yet", and after
 * the first successful poll it stays false forever.
 *
 * **The remaining-time column must tick.** Ten seconds of a frozen clock is
 * long enough to misread who is about to run out, so each row counts down
 * locally between polls, anchored on the server's own `server_now_ms` rather
 * than on the admin's clock (see `useServerClock`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  Laptop,
  Smartphone,
  TimerReset,
  TriangleAlert,
  WifiOff,
} from "lucide-react";
import type { DeviceRole, LiveAttempt, LiveAttemptsResponse } from "@exam/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  Input,
  Select,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { ErrorNote, FixtureBanner, Loading, PageTitle } from "./components/Shell";
import {
  extendAttempt,
  extendExam,
  fetchLiveAttempts,
  forceSubmitAttempt,
  resetAttempt,
} from "./data/client";
import { formatClock, formatDuration } from "./lib/format";

const POLL_INTERVAL_MS = 10_000;

/** Below this the clock turns amber; below one minute it turns red. */
const WARN_REMAINING_MS = 5 * 60_000;
const CRITICAL_REMAINING_MS = 60_000;

/* ------------------------------------------------------------------ */
/* Polling                                                             */
/* ------------------------------------------------------------------ */

interface LiveSnapshot {
  response: LiveAttemptsResponse;
  /** Local `Date.now()` when the response landed, used to correct for clock skew. */
  receivedAt: number;
}

interface LiveFeed {
  snapshot: LiveSnapshot | null;
  error: unknown;
  /** True only until the first response — never again, so the table never blanks. */
  loading: boolean;
  /** Local time of the last successful poll, for the "updated Xs ago" hint. */
  updatedAt: number | null;
  refresh: () => void;
}

/**
 * Polls the live list on an interval, and only on an interval — a poll that is
 * still in flight is never doubled up, and the timer is torn down entirely
 * while the tab is hidden. An invigilator leaves this tab open for hours next
 * to the recordings; a background tab hammering the Worker every 10s for a list
 * nobody is looking at is exactly the kind of cost that goes unnoticed.
 */
function useLiveFeed(examId: string | null): LiveFeed {
  const [snapshot, setSnapshot] = useState<LiveSnapshot | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  // Guards against a slow response from a previous filter landing on top of a
  // newer one, which would show the wrong exam's students.
  const generation = useRef(0);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const current = generation.current;
    try {
      const response = await fetchLiveAttempts(examId ?? undefined);
      if (generation.current !== current) return;
      setSnapshot({ response, receivedAt: Date.now() });
      setUpdatedAt(Date.now());
      setError(null);
    } catch (err: unknown) {
      if (generation.current !== current) return;
      // Keep the last good snapshot on screen: a single failed poll during a
      // sitting is not a reason to take the hall away from the invigilator.
      setError(err);
    } finally {
      inFlight.current = false;
      if (generation.current === current) setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    generation.current++;
    setLoading(snapshot === null);
    void load();

    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Coming back to the tab, whatever is on screen is up to a poll old,
        // so refresh before restarting the cadence.
        void load();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // `snapshot` is read only to decide the initial loading flag; re-running
    // this effect on every poll would restart the interval forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  return { snapshot, error, loading, updatedAt, refresh: () => void load() };
}

/**
 * A once-a-second re-render, so the countdowns move between polls.
 *
 * Returns the server's current time as best we can estimate it: the skew
 * between the admin's clock and `server_now_ms` is measured when a snapshot
 * lands, and every tick after that applies the same offset. The remaining time
 * is therefore always derived from the server's authoritative clock — the local
 * clock only supplies the *elapsed* interval since the poll, which is the one
 * thing it can be trusted for.
 */
function useServerClock(snapshot: LiveSnapshot | null): number | null {
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  if (snapshot === null) return null;
  const skew = snapshot.receivedAt - snapshot.response.server_now_ms;
  return Date.now() - skew;
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type PendingAction =
  | { kind: "extend"; row: LiveAttempt }
  | { kind: "submit"; row: LiveAttempt }
  | { kind: "reset"; row: LiveAttempt };

/** A stable identity for "nothing is running". See the note at its use. */
const EMPTY_ATTEMPTS: LiveAttempt[] = [];

export function LiveMonitorPage() {
  const [examFilter, setExamFilter] = useState<string>("");
  const feed = useLiveFeed(examFilter === "" ? null : examFilter);
  const serverNow = useServerClock(feed.snapshot);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [extendingExam, setExtendingExam] = useState(false);

  // `?? EMPTY_ATTEMPTS`, never `?? []`: a fresh literal here is a new
  // reference on every render, which invalidates the memo below, which changes
  // the effect's dependency, which sets state, which renders again — an
  // unbreakable loop for as long as there is no snapshot, i.e. from mount, and
  // forever if the first poll fails.
  const attempts = feed.snapshot?.response.attempts ?? EMPTY_ATTEMPTS;

  // The filter offers the exams actually being sat, not the whole catalog —
  // an exam nobody is on has nothing to show and nothing to extend.
  const exams = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of attempts) seen.set(row.exam_id, row.exam_title);
    return [...seen].map(([id, title]) => ({ id, title }));
  }, [attempts]);

  // The filter narrows the fetch, so the option for the currently selected exam
  // is the only one left in the list; keeping the last full set means the
  // invigilator can still switch back without clearing the filter first.
  const [examOptions, setExamOptions] = useState<Array<{ id: string; title: string }>>(
    [],
  );
  useEffect(() => {
    if (examFilter !== "") return;
    // Only write when the set actually changed; an unconditional set would
    // re-render on every poll for a list that changes once an hour.
    setExamOptions((current) =>
      current.length === exams.length &&
      current.every((c, i) => c.id === exams[i]?.id && c.title === exams[i]?.title)
        ? current
        : exams,
    );
  }, [examFilter, exams]);

  return (
    <div className="space-y-4">
      <PageTitle
        title="Live"
        meta={<UpdatedHint updatedAt={feed.updatedAt} />}
        actions={
          <Button
            icon={<TimerReset />}
            disabled={examOptions.length === 0}
            onClick={() => setExtendingExam(true)}
          >
            Extend an exam
          </Button>
        }
      />

      <FixtureBanner />

      {feed.error ? <ErrorNote error={feed.error} /> : null}

      <Card>
        <CardHeader
          title="Sitting now"
          description={
            attempts.length === 1
              ? "1 attempt in progress."
              : `${attempts.length} attempts in progress.`
          }
          actions={
            <Select
              aria-label="Filter by exam"
              value={examFilter}
              onChange={(e) => setExamFilter(e.target.value)}
              className="w-56"
            >
              <option value="">All exams</option>
              {examOptions.map((exam) => (
                <option key={exam.id} value={exam.id}>
                  {exam.title}
                </option>
              ))}
            </Select>
          }
        />

        {feed.loading ? (
          <Loading label="Reading the hall…" />
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>Student</TH>
                <TH>Exam</TH>
                <TH align="right">Running</TH>
                <TH align="right">Remaining</TH>
                <TH align="right">Answered</TH>
                <TH align="right">On</TH>
                <TH>Devices</TH>
                <TH align="right">Flags</TH>
                <TH />
              </tr>
            </THead>
            <TBody>
              {attempts.length === 0 ? (
                <TableEmpty colSpan={9}>No exam is being sat right now.</TableEmpty>
              ) : (
                attempts.map((row) => (
                  <LiveRow
                    key={row.attempt_id}
                    row={row}
                    serverNow={serverNow}
                    onAct={setPending}
                  />
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>

      <AttemptActionDialog
        pending={pending}
        onClose={() => setPending(null)}
        onDone={feed.refresh}
      />

      <ExamExtendDialog
        open={extendingExam}
        exams={examOptions}
        onClose={() => setExtendingExam(false)}
        onDone={feed.refresh}
      />
    </div>
  );
}

function UpdatedHint({ updatedAt }: { updatedAt: number | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  if (updatedAt === null) return <>Waiting for the first update…</>;
  const seconds = Math.max(0, Math.round((Date.now() - updatedAt) / 1000));
  return (
    <>
      Refreshes every 10s · updated <span className="tabular">{seconds}</span>s ago
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Row                                                                 */
/* ------------------------------------------------------------------ */

function LiveRow({
  row,
  serverNow,
  onAct,
}: {
  row: LiveAttempt;
  serverNow: number | null;
  onAct: (action: PendingAction) => void;
}) {
  // `remaining_ms` was true at `server_now_ms`; subtract however long ago that
  // was. Clamped at zero — an expired attempt reads 00:00 and waits for the
  // server to auto-submit it, it never counts up into negative time.
  const remaining = useCountdown(row.remaining_ms, serverNow, row.attempt_id);
  const elapsed =
    serverNow !== null && row.started_at !== null ? serverNow - row.started_at : null;

  const remainingTone =
    remaining <= CRITICAL_REMAINING_MS
      ? "text-violation"
      : remaining <= WARN_REMAINING_MS
        ? "text-review"
        : "text-ink";

  const flags = row.severity_counts;

  return (
    <TR className={cn(row.offline && "bg-violation-soft hover:bg-violation-soft")}>
      <TD>
        <Link
          to={`/admin/students/${row.student_id}`}
          className="font-medium text-ink hover:underline"
        >
          {row.student_name}
        </Link>
        {row.attempt_no > 1 ? (
          <span className="ml-1.5 text-2xs text-ink-faint">
            retake {row.attempt_no}
          </span>
        ) : null}
        <ConnectionNote row={row} />
      </TD>

      <TD className="text-ink-muted">
        <Link to={`/admin/attempts/${row.attempt_id}`} className="hover:underline">
          {row.exam_title}
        </Link>
      </TD>

      <TD align="right" className="tabular text-ink-muted">
        {elapsed === null ? "—" : formatDuration(elapsed)}
      </TD>

      <TD align="right">
        <span className={cn("tabular text-sm font-semibold", remainingTone)}>
          {formatClock(remaining)}
        </span>
        {row.extra_time_min > 0 ? (
          <span className="ml-1.5 text-2xs text-ink-faint">
            +{row.extra_time_min}m
          </span>
        ) : null}
      </TD>

      <TD align="right" className="tabular text-ink-muted">
        {row.answered}
        <span className="text-ink-faint"> / {row.question_count}</span>
      </TD>

      <TD align="right" className="tabular text-ink-muted">
        {row.current_order_idx === null ? "—" : row.current_order_idx + 1}
      </TD>

      <TD>
        <Devices row={row} />
      </TD>

      <TD align="right">
        <FlagCounts
          critical={flags.critical}
          warn={flags.warn}
          info={flags.info}
        />
      </TD>

      <TD align="right">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onAct({ kind: "extend", row })}>
            Extend
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onAct({ kind: "submit", row })}>
            End now
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onAct({ kind: "reset", row })}>
            Reset
          </Button>
        </div>
      </TD>
    </TR>
  );
}

/**
 * Counts `remaining_ms` down against the estimated server clock.
 *
 * The baseline is re-anchored on every poll rather than being kept in state,
 * so a grant of extra time shows up on the very next refresh instead of the
 * row continuing to run down a stale local number.
 */
function useCountdown(
  remainingMs: number,
  serverNow: number | null,
  attemptId: string,
): number {
  const anchor = useRef<{ id: string; remaining: number; at: number } | null>(null);

  if (
    serverNow !== null &&
    (anchor.current === null ||
      anchor.current.id !== attemptId ||
      anchor.current.remaining !== remainingMs)
  ) {
    anchor.current = { id: attemptId, remaining: remainingMs, at: serverNow };
  }

  if (serverNow === null || anchor.current === null) return remainingMs;
  return Math.max(0, anchor.current.remaining - (serverNow - anchor.current.at));
}

/**
 * The line that answers "did this student drop out?". Silent when nothing has
 * gone wrong, because a note on every row would train the eye to skip it.
 */
function ConnectionNote({ row }: { row: LiveAttempt }) {
  if (row.offline) {
    return (
      <div className="mt-0.5 flex items-center gap-1 text-2xs font-medium text-violation">
        <WifiOff className="size-3 shrink-0" />
        Laptop has stopped checking in
        {row.offline_ms > 0 ? (
          <span className="tabular"> · {formatDuration(row.offline_ms)}</span>
        ) : null}
      </div>
    );
  }

  if (row.rejoin_count === 0 && row.offline_ms === 0) return null;

  const parts: string[] = [];
  if (row.rejoin_count > 0) parts.push(`left & rejoined ${row.rejoin_count}×`);
  if (row.offline_ms > 0) parts.push(`${formatDuration(row.offline_ms)} offline`);

  return (
    <div className="tabular mt-0.5 text-2xs text-review">{parts.join(" · ")}</div>
  );
}

const DEVICE_ICON = { laptop: Laptop, phone: Smartphone } as const;
const DEVICE_LABEL: Record<DeviceRole, string> = {
  laptop: "Laptop",
  phone: "Phone",
};

function Devices({ row }: { row: LiveAttempt }) {
  // The contract allows a device to be absent from the array entirely (never
  // paired), which is a different thing from paired-and-dead — both read as a
  // grey dot, but the tooltip distinguishes them.
  const byRole = new Map(row.devices.map((d) => [d.role, d]));

  return (
    <div className="flex items-center gap-2">
      {(["laptop", "phone"] as const).map((role) => {
        const device = byRole.get(role);
        // The heartbeat monitor is authoritative about the laptop, so an
        // offline attempt shows a dead laptop even if its last heartbeat has
        // not aged out of `alive` yet.
        const alive = device?.alive === true && !(role === "laptop" && row.offline);
        const Icon = DEVICE_ICON[role];
        const title = !device
          ? `${DEVICE_LABEL[role]} never paired`
          : alive
            ? `${DEVICE_LABEL[role]} online`
            : `${DEVICE_LABEL[role]} offline`;

        return (
          <span
            key={role}
            title={title}
            className={cn(
              "inline-flex items-center gap-1 text-2xs",
              alive ? "text-ink-muted" : "text-ink-faint",
            )}
          >
            <Icon className="size-3.5" />
            <span
              aria-hidden
              className={cn(
                "size-1.5 rounded-full",
                alive ? "bg-answered" : device ? "bg-violation" : "bg-border-strong",
              )}
            />
            <span className="sr-only">{title}</span>
          </span>
        );
      })}
    </div>
  );
}

function FlagCounts({
  critical,
  warn,
  info,
}: {
  critical: number;
  warn: number;
  info: number;
}) {
  if (critical === 0 && warn === 0 && info === 0) {
    return <span className="text-ink-faint">0</span>;
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {critical > 0 ? (
        <Badge tone="violation">
          <TriangleAlert className="size-3" />
          <span className="tabular">{critical}</span>
        </Badge>
      ) : null}
      {warn > 0 ? (
        <Badge tone="review">
          <span className="tabular">{warn}</span>
        </Badge>
      ) : null}
      {info > 0 ? (
        <span className="tabular text-2xs text-ink-faint">{info}</span>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirm dialogs                                                     */
/* ------------------------------------------------------------------ */

/**
 * One dialog for all three per-row interventions. Each of them is something a
 * student notices immediately — their clock jumps, or their paper is taken
 * away — so all three are confirmed, all three record a note, and the two
 * destructive ones spell out what is lost in the body rather than relying on
 * the button colour to carry the warning.
 */
function AttemptActionDialog({
  pending,
  onClose,
  onDone,
}: {
  pending: PendingAction | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [minutes, setMinutes] = useState("10");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Each opening starts from a clean form; a note left over from the previous
  // student would be written into this one's audit trail.
  useEffect(() => {
    if (pending) {
      setMinutes("10");
      setNote("");
      setBusy(false);
    }
  }, [pending]);

  const parsedMinutes = Number.parseInt(minutes, 10);
  const minutesValid = Number.isInteger(parsedMinutes) && parsedMinutes > 0;

  const run = async () => {
    if (!pending) return;
    const { kind, row } = pending;
    const trimmed = note.trim() === "" ? null : note.trim();

    setBusy(true);
    try {
      if (kind === "extend") {
        await extendAttempt(row.attempt_id, parsedMinutes, trimmed);
        toast({
          tone: "success",
          title: `${row.student_name} got ${parsedMinutes} more minutes`,
        });
      } else if (kind === "submit") {
        await forceSubmitAttempt(row.attempt_id, trimmed);
        toast({
          tone: "warning",
          title: `${row.student_name}'s attempt was submitted`,
          description: "It has been graded and closed.",
        });
      } else {
        // The live list is polled, so a row can be several seconds stale;
        // naming the student and status makes the server refuse rather than
        // destroy the wrong sitting.
        await resetAttempt(row.attempt_id, trimmed, {
          studentId: row.student_id,
          status: "in_progress",
        });
        toast({
          tone: "warning",
          title: `${row.student_name}'s attempt was reset`,
          description: "Answers, proctor events and recordings are gone.",
        });
      }
      onDone();
      onClose();
    } catch (err: unknown) {
      toast({
        tone: "error",
        title: "That did not go through",
        description: err instanceof Error ? err.message : "Please try again.",
      });
      setBusy(false);
    }
  };

  const kind = pending?.kind ?? "extend";
  const title =
    kind === "extend"
      ? "Extend this attempt"
      : kind === "submit"
        ? "End this attempt now"
        : "Reset this attempt";

  return (
    <Dialog
      busy={busy}
      open={pending !== null}
      onClose={onClose}
      title={title}
      description={
        pending
          ? `${pending.row.student_name} — ${pending.row.exam_title}`
          : undefined
      }
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={kind === "extend" ? "primary" : "danger"}
            disabled={busy || (kind === "extend" && !minutesValid)}
            onClick={() => void run()}
          >
            {kind === "extend"
              ? "Add the time"
              : kind === "submit"
                ? "End it now"
                : "Reset it"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {kind === "extend" ? (
          <Input
            label="Extra minutes"
            type="number"
            min={1}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            error={minutesValid ? null : "Enter a whole number of minutes."}
            hint="Added to this student's deadline only."
          />
        ) : (
          <p
            className={cn(
              "rounded-md border px-3 py-2 text-xs",
              "border-violation/30 bg-violation-soft text-ink",
            )}
          >
            {kind === "submit"
              ? "The paper closes immediately, is graded on the answers saved so far, and the student cannot go back in."
              : "This destroys the sitting: every answer, every proctor event and every recording for this attempt is deleted. The student starts again from nothing."}
          </p>
        )}

        <Input
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why — e.g. power cut in the lab"
          hint="Kept on the attempt's action log."
        />
      </div>
    </Dialog>
  );
}

/**
 * Extending the whole exam. Deliberately a separate control from the per-row
 * one: it moves every running student's deadline at once, which is the right
 * answer to a hall-wide problem and badly wrong as a fix for one student.
 */
function ExamExtendDialog({
  open,
  exams,
  onClose,
  onDone,
}: {
  open: boolean;
  exams: Array<{ id: string; title: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [examId, setExamId] = useState("");
  const [minutes, setMinutes] = useState("10");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setExamId(exams[0]?.id ?? "");
      setMinutes("10");
      setNote("");
      setBusy(false);
    }
    // The exam list changes on every poll; re-seeding the form mid-edit would
    // move the selection under the invigilator's hand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsedMinutes = Number.parseInt(minutes, 10);
  const minutesValid = Number.isInteger(parsedMinutes) && parsedMinutes > 0;
  const exam = exams.find((e) => e.id === examId) ?? null;

  const run = async () => {
    if (!exam) return;
    setBusy(true);
    try {
      await extendExam(exam.id, parsedMinutes, {
        note: note.trim() === "" ? null : note.trim(),
      });
      toast({
        tone: "success",
        title: `${exam.title} extended by ${parsedMinutes} minutes`,
        description: "Every attempt on it now has more time.",
      });
      onDone();
      onClose();
    } catch (err: unknown) {
      toast({
        tone: "error",
        title: "That did not go through",
        description: err instanceof Error ? err.message : "Please try again.",
      });
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      open={open}
      onClose={onClose}
      title="Extend an exam"
      description="Adds time to every attempt currently running on it."
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={busy || !minutesValid || exam === null}
            onClick={() => void run()}
          >
            Add the time
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Exam"
          value={examId}
          onChange={(e) => setExamId(e.target.value)}
        >
          {exams.map((e) => (
            <option key={e.id} value={e.id}>
              {e.title}
            </option>
          ))}
        </Select>

        <Input
          label="Extra minutes"
          type="number"
          min={1}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          error={minutesValid ? null : "Enter a whole number of minutes."}
        />

        <Input
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why — e.g. projector failure delayed the start"
          hint="Kept on the exam's action log."
        />

        <p className="flex items-start gap-1.5 text-2xs text-ink-faint">
          <Clock className="mt-0.5 size-3 shrink-0" />
          The availability window moves with the duration, so students who have
          not started yet still get the full paper.
        </p>
      </div>
    </Dialog>
  );
}
